import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ActivityIndicator, View} from 'react-native';
import Animated, {FadeIn, FadeOut, Layout} from 'react-native-reanimated';
import {useHeaderHeight} from '@react-navigation/elements';
import {useFocusEffect} from '@react-navigation/native';
import {makeStyles} from '@rneui/themed';
import {observer} from 'mobx-react-lite';

import LoadingImg from '../../../components/LoadingImg';
import CheckoutLogInModal from '../../../components/modals/CheckoutLogInModal';
import CouponModal from '../../../components/modals/CouponModal';
import {MemberPriceCarousel} from '../../../components/ProductCarousel';
import ExclusiveOffers from '../../../components/rewards/ExclusiveOffers';
import ShopLoyaltyEnabled from '../../../components/rewards/ShopLoyaltyEnabled';
import TitleLine from '../../../components/rewards/TitleLine';
import Button from '../../../components/themed/Button';
import ContentView from '../../../components/ui-controls/layout/ContentView';
import Text from '../../../components/ui-controls/Text';
import {useScrollCollapseHandler} from '../../../hooks/useScrollCollapse';
import {useWebScreenDimensions} from '../../../hooks/useWebScreenDimensions';
import {AppRoute} from '../../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../../models/navigationTypes';
import {CartStore} from '../../../stores/cartStore';
import {useStores} from '../../../stores/use-stores';
import theme from '../../../style/theme';
import {OrderLine} from '../../../transport/apiServiceResponse';
import {Analytics} from '../../../utils/analytics/analytics';
import {EventName, EventScreenName, EventSourceType} from '../../../utils/analytics/events';
import {toAUD} from '../../../utils/functions';
import {brandRewardsCopy} from '../../../utils/rewards';
import CartCoupon from '../CartCoupon';
import CartJoinRewards from '../CartJoinRewards';
import CartItemsList from '../components/CartItemsList';
import CouponBlockedNotice from '../components/CouponBlockedNotice';
import {EmptyCart} from '../components/EmptyCart';
import OrderTotals from '../components/OrderTotals';
import {useCheckoutSession} from '../hooks';
import UnifiedCheckoutScreen from '../UnifiedCheckout';

const CartFooterButtons = observer(
  ({
    onCheckoutPress,
    onCouponPress,
    navigation,
  }: {
    cartStore: CartStore;
    onCheckoutPress: () => void;
    onCouponPress: () => void;
    navigation: RouteNavigatorScreenProps<AppRoute.CART>['navigation'];
  }) => {
    const styles = useStyles();
    const {cartStore, rewardsStore} = useStores();

    return (
      <View style={styles.footerButtons} testID="cart-screen-actions-footer-buttons">
        <CartCoupon onCouponPress={onCouponPress} />

        <View style={{gap: 4}}>
          <ShopLoyaltyEnabled>
            {!rewardsStore.hasRewards && <CartJoinRewards navigation={navigation} />}
          </ShopLoyaltyEnabled>
          <OrderTotals
            onRemoveComplete={() => {
              // Delay scroll to next frame — React re-renders after activatedDeals = []
              // causes InvalidDealBanner to unmount, which shifts layout before the scroll target
              requestAnimationFrame(() => {
                const ref = listRef.current as {
                  scrollTo?: (opts: {y: number; animated: boolean}) => void;
                } | null;
                ref?.scrollTo?.({y: offersLayoutY.current, animated: true});
              });
            }}>
            <Button
              // DIS-1608: solid while the coupon blocks checkout. The theme gives a
              // disabled `outlined` button borderWidth 0 and a transparent
              // background (rewardsTheme.ts LMGButton), so the blocked state
              // rendered as bare text with no button shape at all. The other three
              // checkout screens use the solid variant and disable correctly.
              variant={cartStore.couponBlocksCheckout ? 'contained' : 'outlined'}
              onPress={() => onCheckoutPress()}
              disabled={cartStore.couponBlocksCheckout}
              title={
                cartStore.couponBlocksCheckout
                  ? 'ADD MORE TO CONTINUE WITH COUPON'
                  : 'CONTINUE TO CHECKOUT'
              }
            />
            <CouponBlockedNotice />
          </OrderTotals>
        </View>
      </View>
    );
  },
);

const CartScreen = observer(({navigation}: RouteNavigatorScreenProps<AppRoute.CART>) => {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // React requires hooks to be called in the same order on every render
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const styles = useStyles({isWebDesktopScreen});
  const {cartStore, userStore, rewardsStore, offersStore} = useStores();
  const {isLoggedIn} = useCheckoutSession({step: 'cart', skipEmptyCartRedirect: true});
  const listRef = useRef(null);

  // Refresh offers on screen focus so offer state stays in sync with server
  useFocusEffect(
    useCallback(() => {
      offersStore.refreshOffers();
    }, [offersStore]),
  );
  const offersLayoutY = useRef(0);
  const {scrollHandler, resetScrollState} = useScrollCollapseHandler(listRef);
  const headerHeight = useHeaderHeight();
  const [isCouponModalVisible, setCouponModalVisible] = useState(false);
  const [isCheckoutLogInModalVisible, setCheckoutLogInModalVisible] = useState(false);

  // Reset scroll-collapse state when cart becomes empty (e.g. after checkout).
  // Without this, lastScrollY inside useScrollCollapseHandler remembers the
  // pre-checkout scroll position. On next tab focus, useScrollFocusSync restores
  // that stale position, collapsing the header over an empty cart with no
  // scrollable content to generate corrective scroll events.
  const hasOrderLines = !!cartStore.currentOrder?.order_lines?.length;
  useEffect(() => {
    if (!hasOrderLines) {
      resetScrollState();
    }
  }, [hasOrderLines, resetScrollState]);

  useEffect(() => {
    cartStore.getProductsPromotedAtCheckout();
  }, [cartStore]);

  // Web desktop uses unified checkout layout
  if (isWebDesktopScreen) {
    return <UnifiedCheckoutScreen step="cart" navigation={navigation} />;
  }

  // Mobile uses existing implementation below

  const totalsLine = (name: string, amount: number, key: string, forceAmount?: boolean) => {
    if (!name || (!amount && !forceAmount)) {
      return null;
    }
    return (
      <Animated.View
        entering={FadeIn.duration(500).delay(100)}
        exiting={FadeOut.duration(50)}
        key={key}
        style={styles.savingsRow}>
        <Text style={[styles.adjName, styles.adjText]}>
          <Text style={{fontStyle: 'italic', fontWeight: '600'}}>
            {name} {amount < 0 ? 'savings' : ''}
          </Text>
          :
        </Text>
        {!!cartStore.dirtyOrder && (
          <View style={styles.activityHolder}>
            <ActivityIndicator
              style={styles.activityIndicator}
              size="small"
              color={theme.colour.activityIndicator.primary}
            />
          </View>
        )}
        {!!(!cartStore.dirtyOrder && (amount || forceAmount)) && (
          <Text style={[styles.adjAmount, styles.adjText]}>{toAUD(amount, forceAmount)}</Text>
        )}
      </Animated.View>
    );
  };

  const _renderAdjustments = () => {
    const {currentOrder} = cartStore;

    if (!currentOrder) {
      return null;
    }

    const adjustments = currentOrder.adjustments || [];
    const discountsAndCredits = currentOrder.discounts_and_credits || [];
    const chargeLines = currentOrder.order_lines?.filter((ol: OrderLine) => ol.charge) || [];

    if (!adjustments.length && !discountsAndCredits.length && !chargeLines.length) {
      return null;
    }

    return (
      <Animated.View
        layout={Layout.duration(500)}
        style={styles.adjHolder}
        testID="cart-adjustments">
        {adjustments.map((adj: {name: string; amount: {cents_inc_tax: number}}, index: number) =>
          totalsLine(adj.name, adj.amount.cents_inc_tax, `${adj.name}_${index}`),
        )}
        {discountsAndCredits.map((dcl: {narrative: string; amount: number}, index: number) =>
          totalsLine(dcl.narrative, dcl.amount, `${dcl.narrative}_${index}`),
        )}
        {!cartStore.switchingDeliveryPickup
          ? chargeLines.map((ol: OrderLine, index: number) =>
              totalsLine(ol.name, ol.total.cents_inc_tax, `${ol.id}_${index}`, true),
            )
          : null}
      </Animated.View>
    );
  };

  const renderOrderItems = () => (
    <CartItemsList
      orderLines={cartStore.currentOrder?.order_lines}
      emptyComponent={<EmptyCart isWebDesktopScreen={isWebDesktopScreen} />}
      testID="cart-screen-items-list"
    />
  );

  const handleCheckoutPress = () => {
    if (!isLoggedIn) {
      setCheckoutLogInModalVisible(true);
    } else {
      navigation.navigate(AppRoute.ROOT_CHECKOUT_FLOW, {
        screen: AppRoute.CART_ORDER_CHECKOUT,
      });
    }
    Analytics.logEvent({
      eventName: EventName.Checkout,
      parameters: {
        screen_name: EventScreenName.Cart,
        source_type: EventSourceType.Button,
        value: cartStore.currentOrder?.total?.cents_inc_tax / 100,
        tax: cartStore.currentOrder?.total?.cents_tax / 100,
        currency: 'AUD',
      },
    });
  };

  const handleCouponPress = () => setCouponModalVisible(true);

  const renderAdjustmentsAndCartActions = () => (
    <View testID="cart-screen-actions">
      {/*
          * Text Deal name savings: -$0.01
          * Test Deal Name savings: -$0.01
          * Delivery fee: $12.00
        {renderAdjustments()} */}
      {!!cartStore.currentOrder && (
        <View
          testID="cart-screen-actions-footer-wrapper"
          style={[
            {
              width: '100%',
              paddingBottom: cartStore.currentOrder?.adjustments?.length ? 12 : 0,
              minHeight: cartStore.currentOrder?.adjustments?.length ? undefined : 60,
            },
          ]}>
          <CartFooterButtons
            navigation={navigation}
            cartStore={cartStore}
            onCheckoutPress={handleCheckoutPress}
            onCouponPress={handleCouponPress}
          />
        </View>
      )}
    </View>
  );

  const orderLines = cartStore.currentOrder?.order_lines?.filter((line: OrderLine) => !line.charge);

  // Show loading indicator while cart is loading or order is being validated
  // Note: submittingOrder is true during validateOrder() calls (e.g., after time slot selection)
  // Without this check, cart may flash "NO PRODUCTS FOUND" while validation is in progress
  if (cartStore.isLoadingOrder || cartStore.submittingOrder) {
    return (
      <ContentView testID="cart-screen">
        <LoadingImg />
      </ContentView>
    );
  }

  // Show empty state only when not loading and no items
  if (!orderLines || !orderLines.length) {
    return (
      <ContentView testID="cart-screen">
        <EmptyCart isWebDesktopScreen={isWebDesktopScreen} />
        {/*
          DIS-1675: an applied coupon survives the last product being removed, so keep the
          coupon row reachable in the empty state. CouponModal is mounted here as well — the
          one in the non-empty return below is unreachable from this early return.
        */}
        {!!cartStore.currentOrder?.coupon_code && <CartCoupon onCouponPress={handleCouponPress} />}
        <CouponModal open={isCouponModalVisible} callback={() => setCouponModalVisible(false)} />
      </ContentView>
    );
  }

  const handleLoginModalPress = () => {
    setCheckoutLogInModalVisible(false);
    userStore.setPostLoginReturnToScreen(AppRoute.CART);
    navigation.navigate(AppRoute.ACCOUNT_NOT_LOGGED_IN);
  };

  return (
    <ContentView testID="cart-screen">
      <Animated.ScrollView
        ref={listRef}
        testID="checkout-screen-scrollview"
        onScroll={scrollHandler}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={isWebDesktopScreen}
        showsHorizontalScrollIndicator={isWebDesktopScreen}
        contentContainerStyle={{
          ...styles.wrapperStyle,
          paddingTop: headerHeight,
          paddingBottom: 52,
        }}>
        <View style={styles.main}>
          <View>
            <TitleLine title="YOUR CART" />
            {renderOrderItems()}
          </View>
          <ShopLoyaltyEnabled>
            <MemberPriceCarousel
              title={brandRewardsCopy.memberPublicPricingTitle.toUpperCase()}
              member_price="public"
              cartScreen
            />
            {rewardsStore.hasRewards ? (
              <View
                onLayout={e => {
                  offersLayoutY.current = e.nativeEvent.layout.y;
                }}>
                <ExclusiveOffers variant="cart" />
              </View>
            ) : null}
          </ShopLoyaltyEnabled>
        </View>
        <View style={styles.sidebar}>
          {
            /** spacer for desktop */
            isWebDesktopScreen ? (
              <Text variant="heading1" testID="spacer">
                {' '}
              </Text>
            ) : null
          }

          {renderAdjustmentsAndCartActions()}
        </View>
      </Animated.ScrollView>
      <CouponModal open={isCouponModalVisible} callback={() => setCouponModalVisible(false)} />
      <CheckoutLogInModal isVisible={isCheckoutLogInModalVisible} onPress={handleLoginModalPress} />
    </ContentView>
  );
});

export default CartScreen;

const useStyles = makeStyles((theme, {isWebDesktopScreen}: {isWebDesktopScreen?: boolean}) => ({
  wrapperStyle: isWebDesktopScreen
    ? {
        flexDirection: 'row',
        gap: 16,
      }
    : {gap: 16},
  main: isWebDesktopScreen
    ? {
        gap: 16,
        flexShrink: 1,
        flexGrow: 1,
        flexDirection: 'column',
      }
    : {gap: 16},
  sidebar: isWebDesktopScreen
    ? {
        minWidth: '33%',
        maxWidth: '50%',
        flexDirection: 'column',
      }
    : {},
  webDesktopHorizontalContent: {
    flexDirection: 'row',
    gap: 16,
  },
  centerNotice: {
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    marginTop: '50%',
  },
  largeNotice: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.colour.text.heading.primary,
  },
  stickyFooter: {
    // marginHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'white',
  },
  adjHolder: {
    borderTopWidth: 1,
    borderTopColor: 'grey',
    marginHorizontal: 8,
    paddingHorizontal: 8,
    backgroundColor: 'white',
    width: '100%',
  },
  savingsRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    minHeight: 22,
    backgroundColor: 'white',
  },
  adjText: {
    textAlign: 'right',
    color: theme.colour.text.heading.primary,
    fontWeight: '500',
  },
  adjName: {
    flex: 3,
  },
  adjAmount: {
    flex: 1,
  },
  activityHolder: {
    flex: 1,
  },
  activityIndicator: {
    flex: 1,
  },
  footerButtons: {
    // flexDirection: 'row',
    // alignSelf: 'center',
    // justifyContent: 'space-between',
    // width: '100%',
  },
  coupon: {
    flex: 1,
    marginRight: 8,
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: theme.colour.uiControls.primary,
    paddingVertical: 8,
    paddingHorizontal: 4,
    justifyContent: 'center',
    firstLine: {
      color: theme.colour.text.heading.primary,
      textAlign: 'center',
      fontSize: 8,
      fontStyle: 'italic',
    },
    secondLine: {
      color: theme.colour.text.heading.primary,
      textAlign: 'center',
      fontSize: 12,
    },
    noCoupon: {
      color: theme.colour.text.heading.primary,
      fontWeight: '600',
      fontSize: 16,
      textAlign: 'center',
    },
  },
  checkoutButton: {
    flex: 2,
    backgroundColor: theme.colour.background.button.primary,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 4,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  checkoutText: {
    flex: 1.5,
    color: 'white',
    textAlign: 'center',
    fontWeight: '600',
  },
  vertLine: {
    borderRightColor: 'white',
    borderWidth: 0,
    borderRightWidth: 1,
    flex: 0.1,
  },
  checkoutTotal: {
    flex: 1,
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
    fontWeight: '600',
  },
}));
